import { observer } from 'mobx-react-lite'
import { Image, Platform, StyleSheet, View } from 'react-native'
import { Palette, Player, PlayerStates } from '@ppwr/poker'
import { useStore } from '../../../models'
import { color as colorPalette, fontFamily } from '../../../theme'
import { PlayerAvatar } from '../PlayerAvatar'
import { PlayerChips } from '../PlayerChips'
import { SoundWave } from '../SoundWave'
import { PlayerCellCards, PlayerCellCards2 } from './PlayerCellCards'
import { DS } from '../../../design-system'
import React from 'react'
import ClaudiaPic from '../../../../assets/MyFirstGame/claudia.png'
import LesliePic from '../../../../assets/MyFirstGame/leslie.png'
import PetraPic from '../../../../assets/MyFirstGame/petra.png'
import EstherPic from '../../../../assets/MyFirstGame/esther.png'
import { getMyFirstGamePlayers } from '../../../models/game/first-game/MyFirstGameUtils'
import MyFirstGameStepNames from '../../../models/game/first-game/MyFirstGameStepNames'
import { RemoteAudioTrack, RemoteUser } from 'agora-rtc-react'
import { useResponsive } from '@/screens/new-design/hooks'
import { Typography } from '@/design-system/Typography'
import { LayoutShiftAnchor } from '@/hooks/useLayoutShiftRegistry'
import { AvatarTimer } from './AvatarTimer'
import { getLastActionText } from '../utils'
import { useRemoteConfig } from '@/modules/remote-config'

interface PlayerCellContentProps {
  player: Player
  right?: boolean
  bottom?: boolean
  showIndicator?: boolean
}

export const PlayerCellContent = observer(({ player, right, bottom, showIndicator }: PlayerCellContentProps) => {
  const {
    game: {
      actionTime,
      playerActionTimeLeft,
      shouldShowProgressBar,
      currentPlayerId,
      applyOpacityForPlayer,
      isGameOver,
      leaderboardData,
      isRaiseOrFold,
      showCards: showOrHideCards,
      walkthroughType,
      applyOpacityForFirstGame,
      isMyFirstGame,
      stageInternalName,
      isPlayerCardsVisibleByTeacher,
      isPlayerCardsVisibleByEveryone,
      isWalkthrough
    },
    communication: { getIsPlayerOnAVCall, playerAVStatus, talkingPlayerId, showCards, getRemoteUser }
  } = useStore()

  const { is_avatar_timer_enabled } = useRemoteConfig()

  const showActionTimer = shouldShowProgressBar && player.id === currentPlayerId && !player.isFolded()

  const remoteUser = getRemoteUser(Number(player?.id))
  const { verticalScale } = useResponsive()

  const objShowCards = showCards.find((x) => x.userId === player.userId)
  const isPlayerOnAVCall = getIsPlayerOnAVCall(player)
  const playerAVStatusCall = playerAVStatus(player)

  const applyOpacity = applyOpacityForPlayer(player) || applyOpacityForFirstGame()

  const showVideo = isPlayerOnAVCall && playerAVStatusCall?.isPlayerOnVideoCall

  const showAVIndicator =
    playerAVStatusCall?.isPlayerOnAudioCall &&
    !playerAVStatusCall?.isPlayerMuted &&
    !playerAVStatusCall?.isPlayerOnVideoCall

  const hasDealtCards = player.cards.length === 2 || isRaiseOrFold

  const playerChips = isGameOver
    ? leaderboardData?.players?.find((p) => p?.playerId === player?.id)?.chips
    : player.chips

  const renderBotProfilePic = () => {
    const { claudia, petra, leslie, esther } = getMyFirstGamePlayers({ _id: '', name: '' })
    let picture = { uri: '#' }
    let name = ''

    switch (player.id) {
      case claudia.id:
        picture = ClaudiaPic
        name = claudia.name
        break
      case petra.id:
        picture = PetraPic
        name = petra.name
        break
      case esther.id:
        picture = EstherPic
        name = esther.name
        break
      case leslie.id:
        picture = LesliePic
        name = leslie.name
        break
      default:
        break
    }

    return (
      <DS.Frame
        style={{
          opacity: applyOpacity ? 0.4 : 1
        }}
      >
        <Image style={styles.botProfilePic} source={picture} alt={`${name}'s Profile Pic`} width={32} height={32} />
      </DS.Frame>
    )
  }

  const renderPlayerAvatar = () => {
    if (!is_avatar_timer_enabled) {
      if (!showVideo) {
        return null
      }
      return (
        <PlayerAvatar
          name={player.name}
          color={player.color}
          size={4}
          video={showVideo ? { playerId: Number(player.id), right } : undefined}
          applyOpacity={applyOpacity}
        />
      )
    }
    return (
      <AvatarTimer
        avatarProps={{
          name: player.name,
          video: showVideo ? { playerId: Number(player.id), right } : undefined
        }}
        enable={showActionTimer}
        startTime={Math.round(actionTime - playerActionTimeLeft)}
        timeMS={actionTime}
        containerStyle={{
          opacity: applyOpacity ? 0.4 : 1
        }}
      />
    )
  }

  const renderVideoAndCards = () => {
    return (
      <View style={[styles.cardsAndVideoContainer, right ? styles.cardsAndVideoRight : {}]}>
        {isMyFirstGame && renderBotProfilePic()}

        {!isMyFirstGame && renderPlayerAvatar()}

        {hasDealtCards ? (
          <PlayerCellCards2
            showIndicator={showIndicator}
            key={player.cards.join('')}
            cards={player.cards}
            showCards={objShowCards}
            walkthroughType={walkthroughType}
            isMyFirstGame={isMyFirstGame}
            applyOpacity={applyOpacity}
            showOrHideCards={
              showOrHideCards || isPlayerCardsVisibleByTeacher(player.id) || isPlayerCardsVisibleByEveryone(player.id)
            }
            isMyPlayerFolded={player.state.tag === PlayerStates.Folded.tag}
          />
        ) : null}
      </View>
    )
  }

  const color = player.color ?? 'Global/Black'

  return (
    <View style={[styles.container, bottom ? { alignItems: 'center' } : {}]}>
      <View
        style={[styles.playerCardsAndAudioContainer, right ? styles.rigthPlayerAudioCell : { flexDirection: 'row' }]}
      >
        {showAVIndicator ? (
          <View style={[styles.audioContainer, right ? styles.right : {}]}>
            <SoundWave />

            {Platform.OS === 'web' && remoteUser?.rtcUser && (
              <RemoteAudioTrack play={true} track={remoteUser.rtcUser.audioTrack} volume={100} />
            )}
          </View>
        ) : null}
        {renderVideoAndCards()}
      </View>

      <DS.Text
        variant="Caption/Bold"
        style={[
          right ? styles.right : {},
          { opacity: applyOpacity ? 0.4 : 1 },
          {
            fontSize: verticalScale(Number(Typography['Caption/Bold'].fontSize)),
            lineHeight: verticalScale(Number(Typography['Caption/Bold'].lineHeight))
          }
        ]}
        color={talkingPlayerId === player.id ? 'Brand/Orange' : 'Brand/Cream'}
        numberOfLines={1}
      >
        {player.name}
      </DS.Text>

      {player ? (
        <LayoutShiftAnchor name={`myFirstGame.playerCell-${player.name}.chips`}>
          <PlayerChips
            chips={playerChips}
            role={player.role}
            right={right}
            applyOpacity={
              stageInternalName === MyFirstGameStepNames.roles && ['SB', 'BB'].includes(player.role)
                ? false
                : applyOpacity
            }
          />
        </LayoutShiftAnchor>
      ) : null}
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    gap: DS.spacing[4]
  },
  body: {
    flex: 1,
    justifyContent: 'center',
    width: '100%'
  },
  header: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    width: '100%',
    position: 'absolute'
  },
  headerRol: {
    // fontSize: verticalScale(12),
    fontWeight: '700',
    letterSpacing: 1,
    padding: 2,
    color: Palette['Brand/Light Grey']
  },
  headerAction: {
    // fontSize: verticalScale(10),
    fontWeight: '500',
    letterSpacing: -0.5,
    color: colorPalette.palette.black
  },
  cardRol: {
    backgroundColor: colorPalette.palette.lightGrey
    // paddingHorizontal: verticalScale(8),
    // paddingVertical: verticalScale(4)
  },
  name: {
    fontWeight: '700'
  },
  right: {
    textAlign: 'right',
    alignSelf: 'flex-end'
  },
  smallVideoContainer: {
    width: 32,
    height: 32,
    borderRadius: 16
  },
  initialsContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorPalette.palette.smokyBlack
  },
  smallContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorPalette.palette.smokyBlack
  },
  initials: {
    fontFamily: fontFamily.FeatureDeckMedium,
    fontSize: 20,
    color: colorPalette.palette.white
  },
  audioContainer: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#872854'
  },
  cardsAndVideoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 5
  },
  cardsAndVideoRight: {
    flexDirection: 'row-reverse'
  },
  rigthPlayerAudioCell: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start'
  },
  playerCardsAndAudioContainer: {
    gap: 2,
    alignItems: 'center'
  },
  botProfilePic: {
    backgroundColor: DS.palette['Global/White'],
    padding: 6,
    borderWidth: 0.5,
    borderColor: 'gray',
    borderRadius: 20,
    width: 32,
    height: 32
  }
})
